/**
 * Leave this as a useObserver for now
 */
import {classNames, Highlighter, React, useHover, useLocalStore, useAsObservableSource, observer, pluralize, Fragment} from '@api'
import {Classes, Icon, IconName, MenuDivider, MenuItem, Popover, Menu, PopperModifiers, Spinner} from '@blueprintjs/core'

import {IItemRendererProps} from '@blueprintjs/select'
import {Chip, Tooltip} from '@material-ui/core'
import {ViewColumn} from '@material-ui/icons'
import {IMerchCollectionC8, IMerchFolderC8, IMerchPlanC8, IMerchSeasonC8} from '@stores/c8/C8ServicesStore'
import {icons} from '@stores/ui'
import {MyImage, PrimaryIconButton} from 'centric-ui-components'
import {action, computed, toJS, trace} from 'mobx'
import * as css from './CatalogSuggestResult.css'
import {LibraryItemConfig, CatalogImageSourceConfig, DragDropEventHandlers, site, StoresInstance} from '@stores'
import {BuilderCard, BuilderCardLoader, CatalogFieldSearchResult, CatalogItemPreviewMenuItem, FilterValueSearchResult, ICatalogSearchResult, useVipLocalStore, VipLocalStore} from '@modules'
import {CatalogSearchGroupHeader} from './CatalogSearchGroupHeader'
import {Popover2, Tooltip2} from '@api'

class CatalogSuggestResultStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: MyProps) {
    super(stores)
  }

  get searcher() {
    return this.catalog.searcher
  }

  @computed get library() {
    const {item: {value}} = this
    return value?.library
  }

  @computed get searchDragDropEvents() {
    const {item: {value}, library, catalog} = this
    return new DragDropEventHandlers(catalog, {id: value?.value?.id, dragType: library?.index, target: library?.index})
  }

  @computed get loading() {
    const {props: {item: {action, loading}}, searcher, catalog} = this
    return this.props.item?.loading ?? (action === 'search' && searcher.remoteSearcher.loadingSearchTextResultsCount)
  }

  @computed get disabled() {
    const {props: {item: {action}}, catalog, searcher} = this

    if (action == 'clearSelection' || action == 'toggleInlineDetails') {
      return catalog.selection.selectedIds.size == 0
    }

    return false
  }

  @computed get actionLabel() {
    const {props: {item}, catalog, searcher} = this
    if (item.action === 'search') {
      const {searchQueryText, remoteSearcher: {searchTextResultsCount}} = searcher
      return searchQueryText && searchTextResultsCount !== null ? `${searchTextResultsCount} ${pluralize(catalog.catalogItemName, searchTextResultsCount)}` : null
    }

    return item.actionLabel
  }

  @computed get title() {
    const {strings, props: {item, item: {title}}, catalog: {settings: {activeSearchText}}, searcher} = this

    if (item.action === 'search') {
      const {searchQueryText} = searcher
      return searchQueryText ? `${activeSearchText === searchQueryText ? 'Search for ' : 'Search for '} '${searchQueryText}'...` : 'Start typing to search...'
    }

    return item?.type == 'filterValue' && title == '' ? strings.noValue : title
    //return value?.count !== undefined ? `${title} (${value.count})` : title
  }

  @computed get subtitle() { return this.props.item.subtitle}

  @computed get count() {
    const {props: {item: {value}}} = this
    return value?.count
  }

  @computed get hasPopover() {
    const {item: {type, group}} = this
    return this.item.type === 'card' && group === 'catalogItem'
  }

  @computed get hasSubmenu() { return !_.isEmpty(this.children)}

  get children() { return this.props.item.children }

  @computed get isToggledOn() {
    const {
      item, item: {action, type}, actionLabel, catalog,
      filter, catalog: {groupBy, settings, sidebar, presets, sidebar: {left}}, vlp
    } = this
    switch (action) {
      case 'gotoLibraryItem':
        return left.selectedPathItem?.libraryId == item.group && left.selectedPathItem?.where && left.selectedPathItem?.where === [{value: item.value.value._id}]

      case 'gotoField':
        return left.selectedPathItem?.catalogField == item.value?.field

      case 'gotoFilters':
        return left.open && left.tab == 1

      case 'toggleLOD':
        return settings.levelOfDetail == 1

      case 'togglePDF':
        return catalog.isShowingClientPDF

      case 'toggleCardFieldLabels':
        return catalog.settings.showCardLabels

      case 'gotoSeasonSelectorField':
        if (vlp && vlp.isVLPField(type)) {
          return vlp.hierarchy.isSelected(type, item.value.id)
        } else {
          return filter.state.groups[type]?.selected?.has(item.value.name)
        }
        break

      default:
        break
    }
    switch (type) {
      case 'workspace': {
        return item.value._id == this.workspaces.workspaceConfig?._id
      }

      case 'filter': {
        const {selected} = filter.groupFor(item.value.field)
        return item.action == 'filterField' && actionLabel === null && selected?.size > 0
      }

      case 'filterValue': {
        const {parent, value: {id}} = item as FilterValueSearchResult
        return filter?.groupFor(parent?.value?.field)?.selected?.has(id)
      }

      case 'library': {
        const {value: {name}} = item as ICatalogSearchResult<LibraryItemConfig>
        return catalog.isLibrarySelected(name)
      }

      case 'plan': {
        const {value: {id}} = item as ICatalogSearchResult<IMerchPlanC8>
        return _.includes(vlp.hierarchy.selected.plans, id)
      }

      case 'folder': {
        const {value: {id}} = item as ICatalogSearchResult<IMerchFolderC8>
        return vlp.hierarchy.selected.folders?.some(f => _.includes(f, id))
      }

      case 'collection': {
        const {value: {id}} = item as ICatalogSearchResult<IMerchCollectionC8>
        return _.includes(vlp.hierarchy.selected.collections, id)
      }

      case 'merchSeason': {
        const {value: {name}} = item as ICatalogSearchResult<IMerchSeasonC8>
        return filter.state.groups.merchSeason.selected.has(name)
      }

      case 'workspaceView':
        return presets.matchingPreset === item.value

      case 'perspective':
        return catalog.perspective == item.value

      case 'image':
        const {value: {id}} = item as ICatalogSearchResult<CatalogImageSourceConfig>,
          {settings: {imageSource: {ids}}} = catalog
        return ids.length == 0 || ids.includes(id)

      //case 'filter':
      //  return filter.state.groups[item.field?.field].expanded ? 'tick' : 'blank'

      case 'field':
        const {value: {field}} = item as CatalogFieldSearchResult,
          {cardView, groupBy: {groupingByName, catalogItemsGroup}} = catalog

        switch (action) {
          case 'toggleCardField':
            return cardView.all === true || cardView[field] === true

          case 'sort':
            const order = item?.group ? groupingByName.get(item.group) : groupingByName.get(item.esIndex) ?? {orderBy: []} ?? {orderBy: null}
            if (!order?.orderBy) return
            return order?.orderBy?.some(ob => ob.field === field && (item.sortableType === 'custom' ? ob.custom === true : ob.custom === false))

          case 'groupBy':
            return groupBy.hierarchy.indexOf(field) != -1

          case 'gotoField':
            return sidebar.left.selectedPathItem?.libraryId == field

          default:
            break
        }
        break
    }

    return false
  }

  @computed get icon(): IconName | React.ReactNode {
    const {item, item: {action, type, icon}, catalog} = this

    if (icon) return icon

    switch (action) {
      case 'search':
        return icons.search

      case 'resetWorkspace':
        return icons.reset
      case 'gotoFilters':
        return icons.filter

      case 'toggleLOD':
        return <ViewColumn/>

      case 'togglePDF':
        break

      case 'collapseAll':
        return icons.collapseAll

      case 'expandAll':
        return icons.expandAll

      case 'clear':
      case 'clearSelection':
        return icons.clearHierarchy

      case 'toggleCardFieldLabels':
        return icons.cardFieldLabels

      case 'toggleInlineDetails':
        return icons.inlineDetails

      case 'gotoSeasonSelectorField':
      case 'gotoField':
        return this.isToggledOn ? 'tick' : 'blank'

      case 'groupBy':
        const index = catalog.groupBy.hierarchy.indexOf(item.value.field)
        return index == -1 ? 'blank' : index == 0 ? 'header-one' : 'header-two'
    }

    switch (type) {
      case 'library':
      case 'plan':
      case 'folder':
      case 'collection':
      case 'merchSeason':
      case 'workspaceView':
      case 'image':
      case 'filterValue':
      case 'workspace':
        return this.isToggledOn ? 'tick' : 'blank'

      case 'field':
        const {value: {field}, group} = item as CatalogFieldSearchResult, {groupBy: {groupingByName, catalogItemsGroup}} = catalog
        switch (action) {
          case 'toggleCardField':
            return this.isToggledOn ? 'tick' : 'blank'

          case 'sort':
            let icon: 'caret-up' | 'caret-down' | 'blank' = 'blank'
            const order = item.group ? groupingByName.get(item.group) : groupingByName.get(item.esIndex) ?? {orderBy: null}
            if (!order?.orderBy) {
              return icon = 'blank'
            }
            return icon = this.isToggledOn ? order?.orderBy?.find(ob => ob.field == field)?.order == 'asc' ? 'caret-up' : 'caret-down' : 'blank'

          default:
            break
        }
        break
    }

    return 'blank'
  }

  @computed get item() { return this.props.item }

  @computed get groupLabel() {
    const {item: {group, groupLabel}} = this
    return groupLabel ?? group ?? this.label
  }

  /**
   * Shown on the right side of the card
   */
  @computed get label() {
    const {item: {type, action}, actionLabel} = this

    return actionLabel === null || actionLabel === undefined ? null : `${actionLabel ?? (type != 'field' ? _.startCase(type) : _.startCase(action))}`
  }

  @computed get showDividerBefore() {
    const {props: {index, item: {type, group, action, groupLabel}}, catalog: {searcher: {searchText, searchResults}}} = this,
      prior = index > 0 ? searchResults[index - 1] : null
    return /*!parent &&*/ index < searchResults.length &&
                          (
                            (index == 0 && action != 'search' && searchText != null) ||
                            (prior &&
                             //(prior?.type != type && (prior.group != group || !group)) &&
                             (!groupLabel || groupLabel != prior.groupLabel)
                             && (!group || group != prior.group)
                            )
                          )
  }

  SUBMENU_POPOVER_MODIFIERS: PopperModifiers = {
    // 20px padding - scrollbar width + a bit
    flip: {boundariesElement: 'viewport', padding: 20},
    // shift popover up 5px so MenuItems align
    preventOverflow: {boundariesElement: 'viewport', padding: 20}
  }

  @computed get isActiveItem() {
    const {catalog, props: {item, modifiers: {active}}} = this
    return active || catalog.searcher.activeResult == item
  }

  @action maybeRenderPopover = (target: JSX.Element, noPadding = false) => {
    const {hasSubmenu, hasPopover, _childrenComponents} = this
    if (!hasSubmenu && !hasPopover) {
      return target
    }

    return (
      <Popover2
        autoFocus={false}
        captureDismiss={false}
        enforceFocus={false}
        // hoverCloseDelay={0}
        // hoverOpenDelay={100}
        // minimal
        className={css.suggestPopover}
        openOnTargetFocus={true}
        defaultIsOpen={false/*isActiveItem*/} // Annoying to have it auto-open when you're in a modal or scrolled off screen, maybe somehow know it happened due to keyboard movement (would rather use right/left)
        interactionKind={'hover'}
        modifiers={{arrow: {enabled: true}}}
        position={'left'}
        usePortal={true}
        content={hasSubmenu ? <Menu>{_childrenComponents}</Menu> : _childrenComponents}
        popoverClassName={classNames(css.itemPopover, noPadding && css.noPadding, hasSubmenu && Classes.MENU_SUBMENU, hasPopover && css.popoverContent)}
        //target={target}
      >
        {target}
      </Popover2>
    )
  }

  @computed get _childrenComponents() {
    const {props: {item: {children, type, group, value, action}}, props} = this,
      {catalog: {idFor, searcher: {onItemSelect}}} = this

    if (type === 'card') {
      if (group == 'catalogItem') {
        return <BuilderCard data={value} isPopover popoverParent='searcher' showImage/>
      } else if (action === 'gotoLibraryItem') {
        return <BuilderCardLoader isPopover
                                  popoverParent='searcher'
                                  showImage
                                  esIndex={value.library.index}
                                  id={value._id ?? idFor(value.value)}
                                  library={value.library}/>

        //console.warn(`Ignoring children search result due to unknown group ${group}`)
        //return null
        // const library = catalog.libraryById[group]
        // if (!library) {
        //   console.warn(`Unknown search result for popover`, value)
        // } else {
        //
        //   const dataMap = catalog.dataMapForLibrary(library)
        //
        //   return <CatalogDataItem_Mapped data={value} dataMap={dataMap}>
        //     <CatalogDataItemDumb data={value} onClick={() => {}} view={'card'} library={library} label={library.label ?? library.name} draggable={true} field={library.assignTo} renderer={'card'} />
        //   </CatalogDataItem_Mapped>
        // }
      }

      //return null
    }

    if (!_.isEmpty(children)) {
      return <Fragment>
        {children.map((c, i) => <CatalogSuggestResult key={i}
                                                      handleClick={() => onItemSelect(c)}
                                                      query={props.query}
                                                      modifiers={{...props.modifiers, active: false}}
                                                      item={c}
                                                      index={i}/>)}
      </Fragment>
    }

    return null
  }
}

type MyProps = {
  className?: string,
  item: ICatalogSearchResult
} & IItemRendererProps

export const CatalogSuggestResult = React.memo(
  observer<MyProps>(function CatalogSuggestResult(props) {
    const obsProps = useAsObservableSource(props),
      store = useVipLocalStore((ws) => new CatalogSuggestResultStore(ws, obsProps)),
      {
        strings, catalog: {
        cssCardImage,
        workspaceConfig, imageUrlFor,
        searcher
      },
        title, subtitle, actionLabel, searchDragDropEvents: {events, draggable}, hasSubmenu, maybeRenderPopover, icon, isToggledOn, label, showDividerBefore, props: {item, handleClick, modifiers}, disabled, loading
      } = store,
      searchWords = searcher?.searchWords ?? [],
      {type, action, value, sortableType} = item,
      hasAction = false && (action != null || !_.isEmpty(actionLabel))

    const [hoverRef, isHovering] = useHover()

    if (_.isEmpty(item)) {
      return <MenuDivider/>
    }

    let menuItemContents, noSubMenuPadding = false
    if (action === 'gotoCatalogItem') {
      noSubMenuPadding = true
      menuItemContents = <CatalogItemPreviewMenuItem className={css.inlineCatalogItem} onClick={handleClick} data={value} workspace={workspaceConfig}>
        {/*<BuilderCardLoader
        className={css.builderCard}
        hideButtons
        isPopover
        levelOfDetail={1}
        esIndex={catalog.index}
        id={catalog.idFor(value)}/>*/}
      </CatalogItemPreviewMenuItem>
    } else if (action == 'gotoLibraryItem') {
      noSubMenuPadding = true
      menuItemContents = <CatalogItemPreviewMenuItem className={css.inlineCatalogItem} onClick={handleClick} data={value.value} library={value.library}/>
    } else {
      /**
       * The menu might be being shown within a popover so we have to declare another instance - moveto own component
       */
      menuItemContents = (
        <a
          onClick={handleClick}
          className={classNames(
            Classes.MENU_ITEM,
            modifiers.active && Classes.ACTIVE,
            css.menuItem
          )}
          data-type={type}
          data-action={action}
        >
          {value?.images
           ? <MyImage imageUrl={imageUrlFor(_.first(value.images))}/>
           : value?.hex
             ? <MyImage imageUrl={`#${value.hex}`}/>
             : _.isString(icon) ? <Icon className={css.icon} icon={icon as IconName}/> : <span className={css.icon}>{icon}</span>}

          {hasAction &&
		   <span className={css.actionLabel}>
        {_.first(actionLabel) ?? _.startCase(action)}
        </span>}

          <div className={classNames(Classes.FILL, Classes.TEXT_OVERFLOW_ELLIPSIS, css.titleBar)}>
            <Highlighter className={css.title}
                         textToHighlight={title || ''}
                         autoEscape
                         searchWords={item.type === 'action' || item.action === 'search' || item.action == 'clearSearch' ? [] : searchWords}/>
            {subtitle && <Highlighter className={css.subtitle}
			                          textToHighlight={subtitle || ''}
			                          autoEscape
			                          searchWords={searchWords}/>}
            {action === 'gotoSeasonSelectorField' ?
             <Fragment>
               {
                 /*isSearching && !_.isEmpty(item?.selectedVlpChildren) ?
                item.selectedVlpChildren.map((text, j) => <Highlighter className={css.subtitle}
                                                                       key={j}
                                                                       textToHighlight={text || ''}
                                                                       autoEscape
                                                                       searchWords={searchWords}/>)
               : */
                 item?.pathLabel && item.pathLabel != title && <Highlighter className={css.subtitle}
				                                                            textToHighlight={item.pathLabel || ''}
				                                                            autoEscape
				                                                            searchWords={searchWords}/>}
             </Fragment> : null}
          </div>

          {value?.count !== undefined && <span className={css.count}>({value?.count})</span>}
          {(label || loading) &&
		   <span className={classNames(Classes.MENU_ITEM_LABEL)}>
          {loading ? <Spinner className={css.spinner} size={18}/>
                   : <Fragment>{sortableType === 'custom' && <Tooltip2 content={strings.custom}>
		          <PrimaryIconButton className={css.sortIcon} icon={<Icon icon={icon === 'caret-down' ? 'sort-desc' : 'sort-asc'}/>}/>
	          </Tooltip2>}
              <Chip className={css.chip}
                    classes={{label: css.chipLabel}}
                    label={label}/>
              {/*<Highlighter className={css.title}
                                    textToHighlight={typeLabel}
                                    searchWords={searchWords}/>}/>*/}
           </Fragment>}
        </span>}

          {hasSubmenu && <Icon icon='caret-right'/>}
        </a>)
    }

    return (
      <div
        className={css.wrapper}
        style={cssCardImage}
      >
        {showDividerBefore ? <CatalogSearchGroupHeader item={item}/> : null}
        <div
          ref={hoverRef}
          tabIndex={props.index}
          className={classNames(css.root, hasSubmenu && Classes.MENU_SUBMENU)}
          //data-divider={index > 0 && showDividerBefore}
          draggable={draggable}
          data-disabled={disabled}
          onDragStart={events?.onDragStart}
          data-type={type}
          data-toggled={isToggledOn}
          data-action={action}
          data-has-action-label={hasAction}
        >
          {maybeRenderPopover(menuItemContents, noSubMenuPadding)}
        </div>
      </div>)
  }),
  (prevProps, nextProps) => {
    // Doing this because handleClick and modifiers change on every keystroke in blueprint :(
    return prevProps.index == nextProps.index && prevProps.item == nextProps.item && prevProps.modifiers.active == nextProps.modifiers.active && prevProps.modifiers.disabled == nextProps.modifiers.disabled && prevProps.item.title == nextProps.item.title
  })
CatalogSuggestResult['whyDidYouRender'] = true


// WEBPACK FOOTER //
// ./src/modules/catalogSearcher/CatalogSuggestResult.tsx